import { useCallback, useEffect, useState, type ComponentProps } from "react";
import { HistoricalLessonBooking } from "./HistoricalLessonBooking.js";
import { TutorBookLesson as TutorBookLessonCore } from "./TutorBookLessonCore.js";
import "./bookingControls.css";

type TutorBookLessonExperienceProps = ComponentProps<typeof TutorBookLessonCore>;
type TimingMode = "upcoming" | "historical";

function startsInPast(startsAt: string | null | undefined): boolean {
  if (!startsAt) return false;
  const parsed = Date.parse(startsAt);
  return Number.isFinite(parsed) && parsed < Date.now();
}

/**
 * One shared timing choice for every staff lesson-booking entry point.
 *
 * The ordinary availability flow stays forward-looking. Historical creation is
 * deliberately separate because it needs a stronger warning and an exact clash
 * check instead of pretending Amelia's public availability grid is a historical
 * record. A past diary click opens the historical side automatically.
 */
export function TutorBookLessonExperience(props: TutorBookLessonExperienceProps) {
  const initialMode: TimingMode = startsInPast(props.initialStartsAt) ? "historical" : "upcoming";
  const [timingMode, setTimingMode] = useState<TimingMode>(initialMode);
  const [mountedModes, setMountedModes] = useState<Record<TimingMode, boolean>>({
    upcoming: initialMode === "upcoming",
    historical: initialMode === "historical",
  });
  const [mutationBusy, setMutationBusy] = useState<Record<TimingMode, boolean>>({
    upcoming: false,
    historical: false,
  });

  const handleUpcomingBusy = useCallback((busy: boolean) => {
    setMutationBusy((current) =>
      current.upcoming === busy ? current : { ...current, upcoming: busy },
    );
  }, []);
  const handleHistoricalBusy = useCallback((busy: boolean) => {
    setMutationBusy((current) =>
      current.historical === busy ? current : { ...current, historical: busy },
    );
  }, []);
  const anyMutationBusy = mutationBusy.upcoming || mutationBusy.historical;

  useEffect(() => {
    props.onMutationBusyChange?.(anyMutationBusy);
    return () => props.onMutationBusyChange?.(false);
  }, [anyMutationBusy, props.onMutationBusyChange]);

  function chooseTimingMode(nextMode: TimingMode) {
    if (nextMode === timingMode) return;
    setMountedModes((current) =>
      current[nextMode] ? current : { ...current, [nextMode]: true },
    );
    setTimingMode(nextMode);
  }

  return (
    <>
      <div className="panel row tutor-booking-timing" role="group" aria-label="Lesson timing">
        <span className="small muted">When</span>
        <button
          type="button"
          className={timingMode === "upcoming" ? "cal-view is-on" : "cal-view"}
          aria-pressed={timingMode === "upcoming"}
          disabled={anyMutationBusy}
          onClick={() => chooseTimingMode("upcoming")}
        >
          Upcoming
        </button>
        <button
          type="button"
          className={timingMode === "historical" ? "cal-view is-on" : "cal-view"}
          aria-pressed={timingMode === "historical"}
          disabled={anyMutationBusy}
          onClick={() => chooseTimingMode("historical")}
        >
          Past lesson
        </button>
      </div>

      {mountedModes.upcoming && (
        <div hidden={timingMode !== "upcoming"}>
          <TutorBookLessonCore {...props} onMutationBusyChange={handleUpcomingBusy} />
        </div>
      )}
      {mountedModes.historical && (
        <div hidden={timingMode !== "historical"}>
          <HistoricalLessonBooking {...props} onMutationBusyChange={handleHistoricalBusy} />
        </div>
      )}
    </>
  );
}
